import { Combobox, Transition } from '@headlessui/react';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import SelectedOptionSvg from '@/shared/assets/icons/ui-kit/option-selected.svg';

import { Box } from '../box';
import { Icon } from '../icon';
import * as Styled from './searchable-select.styles';
import { Option, SearchableSelectProps } from './searchable-select.types';
import { searchSelectOptions } from './searchable-select.utils';

export default function StyledSearchableSelect({
	options,
	value,
	onChange,
	placeholder = 'Select an option...',
	variant = 'primary',
	Adornment,
	displaySelectedValue = true,
	autoFocus = false,
}: SearchableSelectProps) {
	const { select } = useTheme();
	const { t } = useTranslation();
	const listRef = useRef<HTMLUListElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const [query, setQuery] = useState('');

	const selectedOption = useMemo(() => options.find((option) => option.value === value) ?? null, [options, value]);
	const { filteredOptions, firstMatchIndex } = useMemo(() => searchSelectOptions(query, options), [query, options]);

	const handleChange = (option: Option<string> | null) => {
		if (!option) return;

		onChange(option.value);
	};

	const handleOpen = (open: boolean) => {
		if (open) return;

		setQuery('');
		requestAnimationFrame(() => inputRef.current?.focus());
	};

	useEffect(() => {
		if (firstMatchIndex < 0 || !listRef.current) return;

		const optionElements = listRef.current.querySelectorAll('[data-option]');
		const target = optionElements[firstMatchIndex];

		if (target) {
			target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
		}
	}, [firstMatchIndex]);

	return (
		<Combobox value={selectedOption} by="value" onChange={handleChange}>
			{({ open }) => (
				<Styled.SelectWrapper>
					<Styled.SelectButton open={open} variant={variant} hasValue={Boolean(value)} onClick={() => handleOpen(open)}>
						<Box width="100%">
							{Adornment && <Icon Svg={Adornment} />}
							<Combobox.Input<Option<string>>
								ref={inputRef}
								data-searchable-select-input
								displayValue={(option: Option<string> | null) => (displaySelectedValue ? option?.label || '' : '')}
								onChange={(event) => setQuery(event.target.value)}
								placeholder={placeholder}
								autoFocus={autoFocus}
							/>
						</Box>
						<Combobox.Button as={Styled.IconWrapper} open={open}>
							<Icon Svg={select.arrow} fill={select.fill.default} />
						</Combobox.Button>
					</Styled.SelectButton>

					<Transition
						as={Fragment}
						leave="transition ease-in duration-100"
						leaveFrom="opacity-100"
						leaveTo="opacity-0"
						afterLeave={() => setQuery('')}
					>
						<Combobox.Options as={Styled.DropdownList} ref={listRef} variant={variant}>
							{!filteredOptions.length && <Styled.EmptyOption>{t('text.noMatches')}</Styled.EmptyOption>}
							{filteredOptions.map((option, index) => (
								<Combobox.Option key={`${query}-${index}-${option.value}`} value={option} data-option>
									{({ active, selected }) => (
										<Styled.OptionItem isActive={active} isSelected={selected}>
											{selected && (
												<Styled.CheckIconWrapper>
													<Icon Svg={SelectedOptionSvg} fill="currentColor" />
												</Styled.CheckIconWrapper>
											)}
											<Styled.OptionText isSelected={selected}>{option.label}</Styled.OptionText>
										</Styled.OptionItem>
									)}
								</Combobox.Option>
							))}
						</Combobox.Options>
					</Transition>
				</Styled.SelectWrapper>
			)}
		</Combobox>
	);
}
